/**
 * Centralized analytics tracking for consult actions
 * Wrapper around the main trackEvent utility with consult-specific helpers
 */

import { trackEvent as baseTrackEvent } from "../../../../../utils/mixpanel";

export const trackEvent = baseTrackEvent;

/**
 * Track consult page view
 */
export const trackConsultPageView = (
  dateRange: string,
  viewMode: string,
  viewType: string,
  session: any,
) => {
  trackEvent(
    "Consults Page Viewed",
    {
      "Date Range": dateRange,
      "View Mode": viewMode,
      "View Type": viewType,
    },
    session,
  );
};

/**
 * Track patient selection
 */
export const trackPatientSelection = (patient: any, session: any) => {
  trackEvent(
    "Patient Consult Viewed",
    {
      "Patient ID": patient.clinicOSUUID,
      "Patient Name": patient.name,
      "Procedure Type": patient.procedureType,
      "Visit Type": patient.visitType || "Consultation",
      "Has Photos": patient.hasPhotos,
      "Appointment Time": patient.appointmentTime,
      "Consultation Date": patient.consultationDate,
    },
    session,
  );
};

/**
 * Track view mode change
 */
export const trackViewModeChange = (
  previousView: string,
  newView: string,
  session: any,
) => {
  trackEvent(
    "View Mode Changed",
    {
      "Previous View": previousView,
      "New View": newView,
    },
    session,
  );
};

/**
 * Track search action
 */
export const trackSearch = (
  searchTerm: string,
  resultsCount: number,
  viewMode: string,
  session: any,
) => {
  trackEvent(
    "Searched",
    {
      "Search Term": searchTerm,
      "View Mode": viewMode,
    },
    session,
  );

  if (resultsCount !== undefined) {
    trackEvent(
      "Search Results",
      {
        "Search Term": searchTerm,
        "Results Count": resultsCount,
        "View Mode": viewMode,
      },
      session,
    );
  }
};

/**
 * Track date range navigation
 */
export const trackDateRangeNavigation = (
  eventName: string,
  startDate: string,
  endDate: string,
  viewMode: string,
  offset: number,
  session: any,
) => {
  trackEvent(
    eventName,
    {
      "Start Date": startDate,
      "End Date": endDate,
      "View Mode": viewMode,
      Offset: offset,
    },
    session,
  );
};

/**
 * Track simulation interaction
 */
export const trackSimulationInteraction = (
  action: "Generated" | "Viewed" | "Starred",
  patient: any,
  simulation: any,
  viewMode: string,
  session: any,
) => {
  trackEvent(
    `Simulation ${action}`,
    {
      "Patient ID": patient?.clinicOSUUID,
      "Patient Name": patient?.name,
      "Simulation ID": simulation?.id,
      "Procedure Type": simulation?.procedureType,
      "View Mode": viewMode,
    },
    session,
  );
};

/**
 * Track photo/gallery interaction
 */
export const trackPhotoInteraction = (
  action: "Viewed" | "Uploaded" | "Gallery Opened" | "Gallery Closed",
  patient: any,
  photoData: any,
  session: any,
) => {
  trackEvent(
    `Patient Photo ${action}`,
    {
      "Patient ID": patient?.clinicOSUUID,
      "Patient Name": patient?.name,
      ...photoData,
    },
    session,
  );
};

/**
 * Track similar patients search
 */
export const trackSimilarPatientsSearch = (
  patient: any,
  metadata: any,
  session: any,
) => {
  trackEvent(
    "Similar Patients Search",
    {
      "Patient ID": patient?.clinicOSUUID,
      "Patient Name": patient?.name,
      Age: metadata.age,
      "Body Type": metadata.bodyType,
      "Concern Severity": metadata.concernSeverity,
      "Desired Outcome": metadata.desiredOutcome,
    },
    session,
  );
};

/**
 * Track presentation mode
 */
export const trackPresentationMode = (
  action: "Entered" | "Exited",
  patient: any,
  fromView: string,
  session: any,
) => {
  trackEvent(
    action === "Entered"
      ? "Presentation Page Viewed"
      : "Presentation Mode Exited",
    {
      "Patient ID": patient?.clinicOSUUID,
      "Patient Name": patient?.name,
      "From View": fromView,
      "Procedure Type": patient?.procedureType,
      "Has Photos": patient?.hasPhotos,
    },
    session,
  );
};
